import React from 'react'
import { dateFormat, currencyFormat } from './formatter'

/**
 * Prints a value with a description, handling different data types
 * @param {Object} dataObj - The data object containing the values
 * @param {string} lookup - The key to look up in the data object
 * @param {string} description - The description label
 * @param {string} [secondary] - Optional secondary value key
 * @param {string} [secondType] - Type of secondary value ('date', 'currency', etc)
 * @param {Object} [options] - Additional options
 * @param {string} [options.type] - Primary value type ('date', 'currency', 'int', etc)
 * @param {Object} [options.classes] - CSS class names for the components
 * @returns {JSX.Element}
 * 
 * @example
 * // Basic usage with automatic date detection
 * printValue(data, 'START_DATE', 'Start Date')
 * 
 * @example
 * // Explicit date type specification
 * printValue(data, 'SOME_DATE', 'Some Field', null, null, { type: 'date' })
 * 
 * @example
 * // Currency formatting
 * printValue(data, 'AMOUNT', 'Payment Amount', null, null, { type: 'currency' })
 * 
 * @example
 * // Integer formatting
 * printValue(data, 'COUNT', 'Item Count', null, null, { type: 'int' })
 * 
 * @example
 * // With custom classes
 * printValue(data, 'FIELD', 'Label', null, null, {
 *   classes: {
 *     description: 'descriptionClass',
 *     value: 'valueClass'
 *   }
 * })
 * 
 * @example
 * // With secondary value
 * printValue(data, 'PRIMARY', 'Primary Field', 'SECONDARY', 'date')
 */
export const printValue = (
  dataObj,
  lookup,
  description,
  secondary,
  secondType,
  options = {}
) => {
  const {
    type = '',
    classes = {
    }
  } = options

  const formatValue = (val, valueType) => {
    if (!val && val !== 0) return ''
    
    switch (valueType) {
      case 'date':
        return dateFormat(val)
      case 'currency':
        return currencyFormat(val)
      case 'int':
        // Format as integer
        const intVal = parseInt(val, 10)
        if (!isNaN(intVal)) {
          return intVal.toString()
        }
        return val.toString()
      default:
        // // Check if the value is a number (including string numbers)
        // const numVal = parseFloat(val)
        // if (!isNaN(numVal)) {
        //   // Check if the original value contains a decimal point
        //   const valStr = val.toString()
        //   if (valStr.includes('.')) {
        //     return numVal.toFixed(2)
        //   }
        //   // If it's an integer, return as-is without decimal places
        //   return numVal.toString()
        // }
        // return val.toString()
        return val.toString()
    }
  }

  // Determine if it's a date by explicit type or description
  const isDate = type === 'date' || (!type && description.toLowerCase().includes('date'))
  const primaryType = type || (isDate ? 'date' : '')

  const primaryValue = formatValue(dataObj[lookup], primaryType)
  const secondaryValue = secondary && dataObj[secondary] 
    ? formatValue(dataObj[secondary], secondType || '')
    : null

  return (
    <div>
      <span style={{ fontSize: '0.8rem', color: '#545454' }}>{`${description}:  `}</span>
      <span style={{ fontSize: '0.85rem' }}>
        {primaryValue}
        {secondaryValue && ` (${secondaryValue})`}
      </span>
    </div>
  )
}

/**
 * Prints a value inline with a header
 * @param {string} header - The header/label text
 * @param {string} value - The value to display
 * @param {Object} [options] - Additional options
 * @param {Object} [options.classes] - CSS class names for the components
 * @returns {JSX.Element}
 */
export const printValueInline = (header, value, options = {}) => {
  const {
    classes = {
      description: '',
      value: ''
    }
  } = options

  return (
    <span>
      <span style={{ fontSize: '0.8rem', color: '#545454' }}>{`${header}:  `}</span>
      <span style={{ fontSize: '0.85rem' }}>{`${value}      `}</span>
    </span>
  )
}
